import { createContext, useContext, useEffect, type ReactNode, type MouseEvent } from "react";

import { useOverlayContext } from "@shared/context/OverlayContext";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";

type Handler = (
  e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  payload?: { tagType?: "button" | "link"; analyticsName?: string; trackClick?: boolean }
) => void;

const currentHandler: { current: Handler } = { current: () => undefined };

const clickHandler: Handler = (event, payload) => currentHandler.current(event, payload);

interface ButtonClickContextValue {
  onAnalyticsClick: Handler;
}

const ButtonClickContext = createContext<ButtonClickContextValue>({
  onAnalyticsClick: clickHandler,
});

export const useButtonClickContext = () => useContext(ButtonClickContext);

export const ButtonClickProvider = ({ children }: { children: ReactNode }) => {
  const logAnalytics = useAnalyticsLogger();
  const { analyticsName: overlayName, trackButtonClicks } = useOverlayContext() ?? {};

  const realHandler: Handler = (event, { tagType = "button", analyticsName, trackClick } = {}) => {
    if (!(trackClick || trackButtonClicks)) return;

    logAnalytics({
      eventName: AnalyticsEvent.BUTTON_CLICK,
      payload: {
        name: analyticsName ?? `${overlayName}_${tagType}`,
        label: (event.currentTarget as HTMLElement)?.textContent?.trim(),
        page_url: window.location.href,
        referrer: document.referrer,
        button_type: tagType,
        overlay_name: overlayName,
      },
    });
  };

  useEffect(() => {
    currentHandler.current = realHandler;

    return () => {
      currentHandler.current = () => undefined;
    };
  }, [realHandler]);

  return (
    <ButtonClickContext.Provider value={{ onAnalyticsClick: clickHandler }}>
      {children}
    </ButtonClickContext.Provider>
  );
};
